
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";

interface ProductPurchaseRequest {
  id: string;
  created_at: string;
  product_id: string;
  quantity: number;
  status: string;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

export const ProductAnalytics = () => {
  const { user } = useAuth();

  const { data: purchaseRequests } = useQuery({
    queryKey: ['product-analytics', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('product_purchase_requests')
        .select('id, created_at, product_id, quantity, status')
        .eq('professional_id', user.id);

      if (error) {
        console.error('Error fetching product purchase requests:', error);
        return [];
      }

      return (data || []) as ProductPurchaseRequest[];
    },
    enabled: !!user?.id,
  });

  // Prepare data for charts
  const monthlyData = purchaseRequests?.reduce((acc: any[], request) => {
    const month = new Date(request.created_at).toLocaleDateString('fr-FR', { month: 'long' });
    const existingMonth = acc.find(item => item.month === month);
    
    if (existingMonth) {
      existingMonth.requests += 1;
      existingMonth.quantity += request.quantity;
    } else {
      acc.push({
        month,
        requests: 1,
        quantity: request.quantity
      });
    }
    
    return acc;
  }, []) || [];

  const productData = purchaseRequests?.reduce((acc: any[], request) => {
    const existingProduct = acc.find(item => item.product_id === request.product_id);
    
    if (existingProduct) {
      existingProduct.requests += 1;
    } else {
      acc.push({
        product_id: request.product_id,
        requests: 1
      });
    }
    
    return acc;
  }, []) || [];

  const totalRequests = purchaseRequests?.length || 0;
  const totalQuantity = purchaseRequests?.reduce((sum, request) => sum + request.quantity, 0) || 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total des demandes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalRequests}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Quantité totale
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalQuantity}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Produits demandés
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{productData.length}</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Demandes par mois</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="requests" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Répartition par produit</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={productData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="requests"
                >
                  {productData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
